// MapChart.tsx

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  MouseEvent,
} from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { feature } from "topojson-client";
import { geoCentroid } from "d3-geo";

import { useSpring } from "@react-spring/web";
import Cookies from "js-cookie";

import worldData from "./world-atlas/world/110m.json";
import usData from "./world-atlas/us/10m.json";
import aucaData from "./world-atlas/au-ca/50m.json";
import smallCountriesData from "./world-atlas/small/4cnt.json";

import {
  licensingData,
  fetchData,
  LicensingInfo,
  loadLicensingDataFromGoogleSheet,
} from "../data/licensingData.ts";

import { ArrowLeft, Search, Edit2, X, Check, HelpCircle, BookOpen } from "lucide-react";

import countryMap from "../data/countries.json";

const WORKER_URL = "https://speemap.ryanhardestylewis.workers.dev";

// Convert code => name => name => code
const invertedCountries: Record<string, string> = Object.entries(countryMap).reduce(
  (acc, [code, name]) => {
    acc[name] = code;
    return acc;
  },
  {} as Record<string, string>
);

interface TooltipInfo {
  x: number;
  y: number;
  name: string;
}

interface SearchOption {
  name: string;
  type: "Country" | "State";
  geo: any;
  code: string;
}

interface SelectedRegion {
  name: string;
  type: "Country" | "State";
  code: string;
}

interface MapChartProps {
  hasAccess?: boolean;
  sessionToken?: string;
}

const worldFeatures = feature(worldData, worldData.objects.countries).features;
const usFeatures = feature(usData, usData.objects.states).features;
const auCaFeatures = feature(aucaData, aucaData.objects.au_ca).features;
const smallCountriesFeatures = feature(smallCountriesData, smallCountriesData.objects.countries).features;

const originalCenter: [number, number] = [0, 0];
const originalZoom: number = 1.5;

type MapStyle = "light" | "colorful" | "dark";

// For colorful logic
function parseHexColor(hex: string) {
  if (hex.startsWith("#")) hex = hex.slice(1);
  const bigint = parseInt(hex, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}
function colorDistance(hex1: string, hex2: string) {
  const c1 = parseHexColor(hex1);
  const c2 = parseHexColor(hex2);
  return Math.sqrt(
    (c1.r - c2.r) ** 2 + (c1.g - c2.g) ** 2 + (c1.b - c2.b) ** 2
  );
}
function generateRandomColor(oceanColor: string, minDistance = 40) {
  let color = "";
  while (true) {
    color = `#${Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, "0")}`;
    if (colorDistance(color, oceanColor) > minDistance) {
      break;
    }
  }
  return color;
}

function getFlagSrcForOption(option: SearchOption): string {
  // Helper to get the appropriate flag for a search option
  if (option.type === "Country") {
    if (option.code && option.code !== "unknown") {
      return `/flags-svg/${option.code.toLowerCase()}.svg`;
    } else {
      return `/flags-svg/default.svg`;
    }
  } else {
    // State
    const fileName = `Flag_of_${option.name
      .replace(/\s+/g, "_")
      .replace(/[()]/g, "")}.svg`;
    return `/us-flags-svg/${fileName}`;
  }
}

const MapChart: React.FC<MapChartProps> = ({ hasAccess = false, sessionToken }) => {
  const [mergedData, setMergedData] = useState<LicensingInfo[]>(licensingData);

  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);
  const [sidebarData, setSidebarData] = useState<LicensingInfo | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<SearchOption[]>([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<SelectedRegion | null>(
    null
  );

  // For single field editing
  const [currentField, setCurrentField] = useState<keyof LicensingInfo | null>(null);
  const [originalText, setOriginalText] = useState("");
  const [editedText, setEditedText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // If we update a doc that was no info => re-fetch
  const [wasNoInfoUpdated, setWasNoInfoUpdated] = useState(false);

  // Map style
  const [colorScheme, setColorScheme] = useState<MapStyle>("light");
  const [colorfulMap, setColorfulMap] = useState<Record<string, string>>({});

  // React-spring
  const [{ x, y, zoom }, api] = useSpring(() => ({
    x: originalCenter[0],
    y: originalCenter[1],
    zoom: originalZoom,
    config: { tension: 280, friction: 30 },
  }));

  // 1) On mount => fetch data from Google Sheets
  useEffect(() => {
    async function fetchSheetData() {
      try {
        const data = await loadLicensingDataFromGoogleSheet();
        setMergedData(data);
      } catch (err) {
        console.error("Failed to load from Google Sheet, using fallback data:", err);
        // fallback:
        setMergedData(licensingData);
      }
    }
    fetchSheetData();
  }, []);

  // If user clicks away after no-info => we re-fetch to see new data
  async function refetchAfterNoInfo() {
    try {
      const fetched = await fetchData();
      setMergedData(fetched);
    } catch (err) {
      console.error("Error re-fetching after no-info update:", err);
    }
    setWasNoInfoUpdated(false);
  }

  // On mount => read style from cookie
  useEffect(() => {
    const savedStyle = Cookies.get("mapStyle") as MapStyle | undefined;
    if (savedStyle === "colorful" || savedStyle === "dark") {
      setColorScheme(savedStyle);
    } else {
      setColorScheme("light");
    }
  }, []);
  useEffect(() => {
    Cookies.set("mapStyle", colorScheme, { expires: 365 });
  }, [colorScheme]);

  // Build search
  const searchRef = useRef<HTMLDivElement>(null);
  const searchOptions: SearchOption[] = useMemo(() => {
    const cOpts = worldFeatures.map((geo) => {
      const name = geo.properties.name as string;
      const code = invertedCountries[name] ?? "unknown";
      return {
        name,
        type: "Country",
        geo,
        code,
      };
    });
    const sOpts = usFeatures.map((geo) => ({
      name: geo.properties.name as string,
      type: "State",
      geo,
      code: geo.properties.name as string,
    }));
    const auOpts = auCaFeatures.map((geo) => ({
      name: geo.properties.name as string,
      type: "State",
      geo,
      code: geo.properties.name as string,
    }));
    const smallOpts = smallCountriesFeatures.map((geo) => {
      const name = geo.properties.name as string;
      const code = invertedCountries[name] ?? "unknown";
      return {
        name,
        type: "Country",
        geo,
        code,
      };
    });
    return [...cOpts, ...sOpts, ...auOpts, ...smallOpts];
  }, []);

  // No info if all fields blank
  function isNoInfo(item: LicensingInfo) {
    return (
      item.Applicable === "" &&
      item.StateWebsite === "" &&
      item.ApplyWebsite === "" &&
      item.LicensingBoard === "" &&
      item.Comments === ""
    );
  }

  function getLicensingEntry(regionName: string) {
    return mergedData.find(
      (x) => x.State.toLowerCase() === regionName.toLowerCase()
    );
  }

  function getDefaultFillColor(regionName: string) {
    const entry = getLicensingEntry(regionName);
    if (!entry) {
      if (colorScheme === "dark") return "#3a3a3a";
      return "#E5E7EB";
    }
    if (isNoInfo(entry)) {
      if (colorScheme === "dark") return "#3a3a3a";
      return "#E5E7EB";
    }
    if (
      entry.ApplyWebsite.includes("doesn't license professional engineers") ||
      entry.State.toLowerCase().includes("samoa")
    ) {
      return "#F87171";
    }
    if (colorScheme === "light") {
      return "#60A5FA";
    } else if (colorScheme === "colorful") {
      if (!colorfulMap[regionName]) {
        const c = generateRandomColor("#A1C4FD");
        setColorfulMap((prev) => ({ ...prev, [regionName]: c }));
        return c;
      }
      return colorfulMap[regionName];
    }
    return "#4a4a4a";
  }

  function getFillColor(regionName: string, isSelected: boolean) {
    if (!isSelected) return getDefaultFillColor(regionName);
    if (colorScheme === "colorful") return getDefaultFillColor(regionName);
    return "#FFD700";
  }
  function getStrokeProps(isSelected: boolean) {
    let stroke = "#FFFFFF";
    let strokeWidth = 0.5;
    if (isSelected && colorScheme === "colorful") {
      stroke = "#000000";
      strokeWidth = 4.0;
    }
    return { stroke, strokeWidth };
  }

  async function handleClick(option: SearchOption) {
    // If we updated a no-info doc => re-fetch before switching
    if (wasNoInfoUpdated) {
      await refetchAfterNoInfo();
    }

    const regionName = option.name;
    const entry = getLicensingEntry(regionName);
    if (entry) {
      setSidebarData(entry);
    } else {
      setSidebarData({
        State: regionName,
        Applicable: "",
        StateWebsite: "",
        ApplyWebsite: "",
        LicensingBoard: "",
        Comments: "",
      });
    }
    setSelectedRegion({ name: regionName, type: option.type, code: option.code });

    if (currentField != null) handleFieldSave();

    const centroid = geoCentroid(option.geo);
    goTo(centroid, 4);
  }
  const goTo = useCallback(
    (center: [number, number], z: number) => {
      api.start({ x: center[0], y: center[1], zoom: z });
    },
    [api]
  );

  async function handleCloseSidebar() {
    if (currentField != null) handleFieldSave();

    if (wasNoInfoUpdated) {
      await refetchAfterNoInfo();
    }

    setSidebarData(null);
    setSelectedRegion(null);
    goTo(originalCenter, originalZoom);
  }

  function handleMouseEnter(geo: any, evt: MouseEvent<SVGPathElement>) {
    setTooltip({ name: geo.properties.name, x: evt.clientX, y: evt.clientY });
  }
  function handleMouseLeave() {
    setTooltip(null);
  }

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions([]);
      return;
    }
    const filtered = searchOptions.filter((opt) =>
      opt.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, searchOptions]);

  useEffect(() => {
    function outsideClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsDropdownVisible(false);
      }
    }
    document.addEventListener("mousedown", outsideClick);
    return () => {
      document.removeEventListener("mousedown", outsideClick);
    };
  }, []);

  const mapBackground =
    colorScheme === "light"
      ? "#FFFFFF"
      : colorScheme === "colorful"
      ? "#A1C4FD"
      : "#1a1a1a";

  const logoSrc = colorScheme === "dark" ? "spee_dark.png" : "spee.png";

  function startEditingField(field: keyof LicensingInfo, val: string) {
    if (currentField && currentField !== field) {
      handleFieldSave();
    }
    setCurrentField(field);
    setOriginalText(val);
    setEditedText(val);
  }
  useEffect(() => {
    if (currentField) {
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [currentField]);

  function handleFieldRevert() {
    setEditedText(originalText);
    setCurrentField(null);
  }

  async function handleFieldSave() {
    if (!sidebarData || !selectedRegion) {
      setCurrentField(null);
      return;
    }
    let newVal = editedText.trim() === "" ? "N/A" : editedText.trim();
    // replace " ; " => newline
    newVal = newVal.replace(/ ?; ?/g, "\n");

    if (newVal !== originalText) {
      console.log("Sending to API, new text:", newVal);
      if (hasAccess && sessionToken) {
        try {
          const body = {
            sessionToken,
            placeName: sidebarData.State,
            fieldName: currentField,
            newValue: newVal,
          };
          const resp = await fetch(`${WORKER_URL}/update`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!resp.ok) {
            console.error("Error from /update:", await resp.text());
          } else {
            console.log("Successfully updated Firestore doc:", sidebarData.State);
            // If doc was no-info => now set wasNoInfoUpdated => true
            if (isNoInfo(sidebarData)) {
              setWasNoInfoUpdated(true);
              if (colorScheme === "colorful") {
                const c = generateRandomColor("#A1C4FD");
                setColorfulMap((prev) => ({ ...prev, [sidebarData.State]: c }));
              }
            }
          }
        } catch (err) {
          console.error("Error calling /update:", err);
        }
      }
      sidebarData[currentField!] = newVal;
    }
    setCurrentField(null);
  }

  function handleFieldChange(val: string) {
    setEditedText(val);
  }

  function renderFinalContent(content: string) {
    // On display => also replace " ; " => newline
    const replaced = content.replace(/ ?; ?/g, "\n");
    const lines = replaced.split("\n");

    // For link colors, depending on dark vs. light
    const linkClass =
      colorScheme === "dark"
        ? "text-[rgb(126,162,241)]" // Dark mode => "rgb(126,162,241)"
        : "text-blue-600"; // Light mode => normal "text-blue-600"

    return (
      <>
        {lines.map((line, idx) => {
          const tokens = line.split(" ");
          return (
            <div key={idx} style={{ whiteSpace: "pre-wrap" }}>
              {tokens.map((token, i) => {
                const lower = token.toLowerCase();
                const isLink = lower.startsWith("http") || lower.startsWith("www");
                if (isLink) {
                  const href = lower.startsWith("www") ? `http://${token}` : token;
                  const visible =
                    token.length > 25 ? token.substring(0, 25) + "..." : token;
                  return (
                    <a
                      key={i}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${linkClass} underline`}
                    >
                      {visible + " "}
                    </a>
                  );
                } else {
                  return <span key={i}>{token + " "}</span>;
                }
              })}
            </div>
          );
        })}
      </>
    );
  }

  function noInfoButEditable(item: LicensingInfo) {
    return isNoInfo(item) && hasAccess;
  }
  function getCurrentFieldText(field: keyof LicensingInfo) {
    if (!sidebarData) return "";
    if (currentField === field) return editedText;
    if (noInfoButEditable(sidebarData)) {
      return "N/A";
    }
    return sidebarData[field] || "";
  }

  // --------------------------------------
  // Updated function to render each field:
  // --------------------------------------
  function renderSidebarField(field: keyof LicensingInfo, label: string) {
    const isEditing = currentField === field;
    const displayedVal = getCurrentFieldText(field);

    // We only want tooltips for "Applicable" and "Apply Website"
    // (but you could easily extend this logic for other fields).
    let tooltipMessage: string | null = null;
    if (label === "Applicable") {
      tooltipMessage =
        "Does the area have a licensing agency related to oil and gas industry?";
    } else if (label === "Apply Website") {
      tooltipMessage = "Website to Apply for Registration";
    }

    return (
      <li className="flex flex-col" key={field}>
        <div className="flex items-center">
          <strong>{label}:</strong>

          {/* If we have a tooltip message, show question mark icon */}
          {tooltipMessage && (
            <div className="relative group">
              <HelpCircle
                className={`w-4 h-4 ml-2 cursor-pointer ${
                  colorScheme === "dark" ? "text-white" : "text-gray-600"
                }`}
              />
              {/* Tooltip box (only visible on hover) */}
              <div
                className="absolute hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded-md"
                style={{
                  transform: "translateY(-200%)",
                  whiteSpace: "nowrap",
                  zIndex: 9999,
                }}
              >
                {tooltipMessage}
              </div>
            </div>
          )}

          {hasAccess && (
            <>
              {!isEditing && (
                <Edit2
                  className={`w-4 h-4 ml-2 cursor-pointer ${
                    colorScheme === "dark" ? "text-white" : "text-gray-600"
                  }`}
                  onClick={() => startEditingField(field, displayedVal)}
                />
              )}
              {isEditing && (
                <div className="flex items-center space-x-2 ml-2">
                  <X
                    className={`w-4 h-4 cursor-pointer ${
                      colorScheme === "dark" ? "text-white" : "text-gray-600"
                    }`}
                    onClick={handleFieldRevert}
                  />
                  <Check
                    className={`w-4 h-4 cursor-pointer ${
                      colorScheme === "dark" ? "text-white" : "text-gray-600"
                    }`}
                    onClick={handleFieldSave}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {isEditing ? (
          <textarea
            ref={textareaRef}
            rows={4}
            value={editedText}
            onChange={(e) => handleFieldChange(e.target.value)}
            className="ml-2 border border-gray-300 rounded px-2 py-1 mt-1 w-full"
            style={{
              backgroundColor: colorScheme === "dark" ? "#2b2b2b" : "#ffffff",
              color: colorScheme === "dark" ? "#ffffff" : "#000000",
            }}
          />
        ) : (
          <div className="ml-2" style={{ whiteSpace: "pre-wrap" }}>
            {renderFinalContent(displayedVal || "N/A")}
          </div>
        )}
      </li>
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Left Container */}
      <div className="absolute top-4 left-4 z-20 flex flex-col items-start space-y-4">
        <img src={logoSrc} alt="Logo" className="w-32 h-auto" />
        <h1
          className="text-2xl font-bold drop-shadow-lg flex justify-start items-start text-left text-black"
          style={{
            textShadow:
              "2px 2px 0 white, -2px 2px 0 white, 2px -2px 0 white, -2px -2px 0 white, 0 2px 0 white, 0 -2px 0 white, 2px 0 0 white, -2px 0 0 white",
          }}
        >
          Professional Engineering
          <br />
          Licensing Map
        </h1>

        {/* Search */}
        <div className="relative w-64" ref={searchRef}>
          <div className="relative">
            <input
              type="text"
              placeholder="Search for a place..."
              className="px-4 py-2 border rounded-full w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ borderRadius: "30px" }}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsDropdownVisible(true);
              }}
              onFocus={() => setIsDropdownVisible(true)}
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500" />

            {/* SEARCH DROPDOWN with flags */}
            {isDropdownVisible && filteredOptions.length > 0 && (
              <ul className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md max-h-60 overflow-y-auto shadow-lg">
                {filteredOptions.slice(0, 10).map((option, index) => (
                  <li
                    key={index}
                    className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      handleClick(option);
                      setSearchTerm(option.name);
                      setIsDropdownVisible(false);
                    }}
                  >
                    {/* RE-STORED FLAG IMAGE */}
                    <img
                      src={getFlagSrcForOption(option)}
                      alt={`${option.name} flag`}
                      className="w-4 h-3 mr-2 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/flags-svg/default.svg";
                      }}
                    />
                    <span className="flex-grow">{option.name}</span>
                    <span className="text-sm text-gray-500 ml-2">{option.type}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Right map style dropdown */}
      <div
        className="absolute top-4 right-4 z-20 bg-white p-2 rounded shadow flex flex-col space-y-2"
        style={{ minWidth: "120px" }}
      >
        <label className="font-bold text-sm">Map Style</label>
        <select
          className="border border-gray-300 rounded px-2 py-1 focus:outline-none"
          value={colorScheme}
          onChange={(e) => setColorScheme(e.target.value as MapStyle)}
        >
          <option value="light">Light</option>
          <option value="colorful">Colorful</option>
          <option value="dark">Dark</option>
        </select>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full shadow-lg transform transition-transform duration-500 ${
          sidebarData ? "translate-x-0" : "-translate-x-full"
        } w-80 md:w-96 z-30 ${
          colorScheme === "dark" ? "bg-[#2b2b2b] text-white" : "bg-white text-black"
        }`}
      >
        {sidebarData && (
          <div className="p-4 relative h-full overflow-visible">
            {/* Possibly show a bigger flag for the place */}
            <img
              src={(() => {
                if (!selectedRegion) return "";
                if (selectedRegion.type === "State") {
                  const fileName = `Flag_of_${selectedRegion.name
                    .replace(/\s+/g, "_")
                    .replace(/[()]/g, "")}.svg`;
                  return `/us-flags-svg/${fileName}`;
                } else {
                  if (selectedRegion.code === "unknown") {
                    return `/flags-svg/default.svg`;
                  }
                  return `/flags-svg/${selectedRegion.code.toLowerCase()}.svg`;
                }
              })()}
              alt={`${sidebarData.State} flag`}
              className="w-full h-32 object-cover mb-4 rounded-md"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/flags-svg/default.svg";
              }}
            />

            {/* CHANGED: The state name plus the new menu icon with tooltip */}
            <div className="flex items-center mb-4">
              <h1 className="text-2xl font-bold flex-1">{sidebarData.State}</h1>
              <div className="relative group ml-2">
                {/* The menu icon */}
                <BookOpen className="w-4 h-4 cursor-pointer" />
                {/* Tooltip for nomenclature */}
                <div
                  className="absolute hidden group-hover:block z-50
                             bg-white text-black text-xs px-2 py-1
                             rounded-md shadow-md"
                  style={{
                    transform: "translateY(-130%)",
                    whiteSpace: "nowrap",
                  }}
                >
                  <p className="font-bold">Nomenclature</p>
                  <p>PE = Professional Engineer</p>
                  <p>N/A = not applicable, unknown</p>
                </div>
              </div>
            </div>

            <ul className="space-y-2">
              {(
                <>
                  {renderSidebarField("Applicable", "Applicable")}
                  {renderSidebarField("StateWebsite", "State/Province/Country Website")}
                  {renderSidebarField("ApplyWebsite", "Apply Website")}
                  {renderSidebarField("LicensingBoard", "Helpful Websites")}
                  {renderSidebarField("Comments", "Comments")}
                </>
              )}
            </ul>

            {/* Close button */}
            <button
              className="absolute top-4 right-4 w-10 h-10 rounded-full shadow-lg flex items-center justify-center"
              style={{
                backgroundColor: colorScheme === "dark" ? "#ffffff" : "#ffffff",
              }}
              onClick={async () => {
                if (currentField) {
                  await handleFieldSave();
                }
                if (wasNoInfoUpdated) {
                  await refetchAfterNoInfo();
                }
                setSidebarData(null);
                setSelectedRegion(null);
                goTo(originalCenter, originalZoom);
              }}
              aria-label="Close Sidebar"
            >
              <ArrowLeft
                className={`${
                  colorScheme === "dark" ? "text-gray-800" : "text-gray-600"
                }`}
              />
            </button>
          </div>
        )}
      </div>

      {/* The Map */}
      <ComposableMap
        projection="geoEquirectangular"
        projectionConfig={{ scale: 125 }}
        className="w-full h-full"
        data-tip=""
        style={{
          backgroundColor: mapBackground,
        }}
      >
        <ZoomableGroup
          center={[x.get(), y.get()]}
          zoom={zoom.get()}
          minZoom={0.8}
          maxZoom={8}
          translateExtent={[
            [100, 100],
            [700, 500],
          ]}
          disablePanning={false}
          disableZoom={false}
        >
          {/* World */}
          <Geographies
            geography={{ type: "FeatureCollection", features: worldFeatures }}
          >
            {({ geographies }) =>
              geographies.map((geo) => {
                const countryName = geo.properties.name as string;
                const isSelected =
                  selectedRegion?.name === countryName &&
                  selectedRegion.type === "Country";

                const fillColor = getFillColor(countryName, isSelected);
                const { stroke, strokeWidth } = getStrokeProps(isSelected);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke={stroke}
                    style={{
                      default: {
                        outline: "none",
                        cursor: "pointer",
                        vectorEffect: "non-scaling-stroke",
                        strokeWidth,
                      },
                      hover: {
                        outline: "none",
                        opacity: 0.8,
                        cursor: "pointer",
                        vectorEffect: "non-scaling-stroke",
                        strokeWidth,
                      },
                      pressed: {
                        outline: "none",
                        vectorEffect: "non-scaling-stroke",
                      },
                    }}
                    onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => {
                      const found = searchOptions.find(
                        (o) => o.name === countryName && o.type === "Country"
                      );
                      if (found) handleClick(found);
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* US States */}
          <Geographies
            geography={{ type: "FeatureCollection", features: usFeatures }}
          >
            {({ geographies }) =>
              geographies.map((geo) => {
                const stateName = geo.properties.name as string;
                const isSelected =
                  selectedRegion?.name === stateName &&
                  selectedRegion.type === "State";

                const fillColor = getFillColor(stateName, isSelected);
                const { stroke, strokeWidth } = getStrokeProps(isSelected);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke={stroke}
                    style={{
                      default: {
                        outline: "none",
                        cursor: "pointer",
                        vectorEffect: "non-scaling-stroke",
                        strokeWidth,
                      },
                      hover: {
                        outline: "none",
                        opacity: 0.8,
                        cursor: "pointer",
                        vectorEffect: "non-scaling-stroke",
                        strokeWidth,
                      },
                      pressed: {
                        outline: "none",
                        vectorEffect: "non-scaling-stroke",
                      },
                    }}
                    onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => {
                      const found = searchOptions.find(
                        (o) => o.name === stateName && o.type === "State"
                      );
                      if (found) handleClick(found);
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Canada & Australia */}
          <Geographies
            geography={{ type: "FeatureCollection", features: auCaFeatures }}
          >
            {({ geographies }) =>
              geographies.map((geo) => {
                const subName = geo.properties.name as string;
                const isSelected =
                  selectedRegion?.name === subName &&
                  selectedRegion.type === "State";

                const fillColor = getFillColor(subName, isSelected);
                const { stroke, strokeWidth } = getStrokeProps(isSelected);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke={stroke}
                    style={{
                      default: {
                        outline: "none",
                        cursor: "pointer",
                        vectorEffect: "non-scaling-stroke",
                        strokeWidth,
                      },
                      hover: {
                        outline: "none",
                        opacity: 0.8,
                        cursor: "pointer",
                        vectorEffect: "non-scaling-stroke",
                        strokeWidth,
                      },
                      pressed: {
                        outline: "none",
                        vectorEffect: "non-scaling-stroke",
                      },
                    }}
                    onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => {
                      const found = searchOptions.find(
                        (o) => o.name === subName && o.type === "State"
                      );
                      if (found) handleClick(found);
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Small Countries */}
          <Geographies
            geography={{ type: "FeatureCollection", features: smallCountriesFeatures }}
          >
            {({ geographies }) =>
              geographies.map((geo) => {
                const countryName = geo.properties.name as string;
                const isSelected =
                  selectedRegion?.name === countryName &&
                  selectedRegion.type === "Country";

                const fillColor = getFillColor(countryName, isSelected);
                const { stroke, strokeWidth } = getStrokeProps(isSelected);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke={stroke}
                    style={{
                      default: {
                        outline: "none",
                        cursor: "pointer",
                        vectorEffect: "non-scaling-stroke",
                        strokeWidth,
                      },
                      hover: {
                        outline: "none",
                        opacity: 0.8,
                        cursor: "pointer",
                        vectorEffect: "non-scaling-stroke",
                        strokeWidth,
                      },
                      pressed: {
                        outline: "none",
                        vectorEffect: "non-scaling-stroke",
                      },
                    }}
                    onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => {
                      const found = searchOptions.find(
                        (o) => o.name === countryName && o.type === "Country"
                      );
                      if (found) handleClick(found);
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute bg-gray-800 text-white text-sm px-2 py-1 rounded pointer-events-none"
          style={{
            whiteSpace: "nowrap",
            zIndex: 40,
            left: `${tooltip.x + 10}px`,  // Add these two lines
            top: `${tooltip.y + 10}px`    // to position relative to cursor
          }}
        >
          {tooltip.name}
        </div>
      )}
    </div>
  );
};

export default MapChart;
